import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Star, MessageSquare, Send, Phone } from 'lucide-react'
import ReviewCard from '../components/ReviewCard'
import SEO from '../components/SEO'

export default function Reviews() {
  const [reviews, setReviews] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeCategory, setActiveCategory] = useState('Alle')

  useEffect(() => {
    fetch('/_api/public/content/reviews')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setReviews(data)
        }
        setLoading(false)
      })
      .catch((err) => {
        console.error('Fejl ved hentning af anmeldelser:', err)
        setLoading(false)
      })
  }, [])

  // Filter reviews by category
  const categories = [
    { id: 'Alle', label: 'Alle anmeldelser' },
    { id: 'bryllup', label: 'DJ til bryllup' },
    { id: 'foedselsdag', label: 'DJ til fødselsdag' },
    { id: 'firmafest', label: 'DJ til firmafest' },
  ]

  const filteredReviews = reviews.filter((r) => {
    if (activeCategory === 'Alle') return true
    const type = (r.event_type?.da || r.event_type || '').toLowerCase()
    if (activeCategory === 'bryllup') return type.includes('bryllup')
    if (activeCategory === 'foedselsdag') return type.includes('fødselsdag') || type.includes('foedselsdag') || type.includes('80') || type.includes('års') || type.includes('party')
    if (activeCategory === 'firmafest') return type.includes('firma') || type.includes('julefrokost') || type.includes('corporate')
    return true
  })

  const reviewsSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ItemPage",
        "@id": "https://www.djron9.dk/anmeldelser/#webpage",
        "url": "https://www.djron9.dk/anmeldelser/",
        "name": "Kundeanmeldelser & Erfaringer | DJ Ron9",
        "description": "Læs hvad mine kunder siger om deres fest med DJ Ron9. 5.0 ud af 5 stjerner baseret på ægte anmeldelser fra bryllupper, firmafester og fødselsdage.",
        "breadcrumb": {
          "@id": "https://www.djron9.dk/anmeldelser/#breadcrumb"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://www.djron9.dk/anmeldelser/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Forside",
            "item": "https://www.djron9.dk/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Anmeldelser",
            "item": "https://www.djron9.dk/anmeldelser/"
          }
        ]
      },
      {
        "@type": "EntertainmentBusiness",
        "name": "DJ Ron9",
        "url": "https://www.djron9.dk/",
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "5.0",
          "bestRating": "5",
          "worstRating": "1",
          "ratingCount": reviews.length > 0 ? String(reviews.length) : "25"
        },
        "review": reviews.slice(0, 10).map((r) => ({
          "@type": "Review",
          "author": {
            "@type": "Person",
            "name": r.name || "Kunde"
          },
          "reviewRating": {
            "@type": "Rating",
            "ratingValue": String(r.rating || 5),
            "bestRating": "5"
          },
          "reviewBody": r.text?.da || r.text || ""
        }))
      }
    ]
  }

  return (
    <div className="bg-black text-white min-h-screen">
      <SEO
        title="Kundeanmeldelser & Erfaringer | DJ Ron9"
        description="Læs hvad mine kunder siger om deres fest med DJ Ron9. 5.0 ud af 5 stjerner baseret på ægte anmeldelser fra bryllupper, firmafester og fødselsdage."
        canonical="/anmeldelser/"
        ogImage="https://assets.auxodev.dk/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/24ca6849-ad7d-45c7-801d-a058c82542ed.jpg"
        schema={reviewsSchema}
      />
      {/* Header Section */}
      <section className="relative flex min-h-[35vh] sm:min-h-[50vh] items-center justify-center overflow-hidden py-12 sm:py-20">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/f44d6781-9183-40b0-88fc-e2c1542ad3e6/24ca6849-ad7d-45c7-801d-a058c82542ed.jpg"
            alt="Kundeanmeldelser"
            width="1600"
            height="500"
            fetchPriority="high"
            decoding="async"
            className="h-full w-full object-cover opacity-60 scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h1 className="font-serif text-4xl font-extrabold tracking-tight sm:text-5xl text-white">
            Kundeanmeldelser
          </h1>

          <p className="mx-auto mt-4 max-w-2xl text-base text-zinc-400">
            Læs hvad mine fantastiske kunder siger om deres oplevelse med DJ Ron9. Troværdighed, energi og uforglemmelige fester er min højeste prioritet.
          </p>

          {/* Rating Summary Card */}
          <div className="mt-10 inline-flex flex-col items-center justify-center">
            <div className="flex items-center gap-1 text-amber-400">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <span className="mt-2 text-2xl font-bold text-white">5.0 ud af 5.0</span>
            <span className="text-xs text-zinc-500 mt-1">
              Baseret på 100% tilfredse kunder og over 25 års fester
            </span>
          </div>

          {/* Contact CTA Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#bb9b56] px-8 py-3.5 text-sm font-bold text-white shadow-lg hover:bg-[#a88645] transition-all"
            >
              <Send className="h-4 w-4" />
              Få et uforpligtende tilbud
            </Link>
            <div
              className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-full border border-zinc-700 bg-zinc-900/80 px-8 py-3.5 text-sm font-bold text-white"
            >
              <Phone className="h-4 w-4 text-[#bb9b56]" />
              50 99 82 82
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Grid Section */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Category Filter Tabs */}
          <div className="mb-12 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            {categories.map((cat) => {
              const count = reviews.filter((r) => {
                if (cat.id === 'Alle') return true
                const type = (r.event_type?.da || r.event_type || '').toLowerCase()
                if (cat.id === 'bryllup') return type.includes('bryllup')
                if (cat.id === 'foedselsdag') return type.includes('fødselsdag') || type.includes('foedselsdag') || type.includes('80') || type.includes('års') || type.includes('party')
                if (cat.id === 'firmafest') return type.includes('firma') || type.includes('julefrokost') || type.includes('corporate')
                return true
              }).length

              const isActive = activeCategory === cat.id
              return (
                <button
                  key={cat.id}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all duration-200 cursor-pointer ${
                    isActive
                      ? 'bg-[#bb9b56] text-white shadow-lg shadow-amber-950/40 font-semibold'
                      : 'border border-zinc-800 bg-zinc-900/60 text-zinc-400 hover:border-zinc-700 hover:text-white hover:bg-zinc-800'
                  }`}
                >
                  <span>{cat.label}</span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs ${
                      isActive
                        ? 'bg-black/30 text-white'
                        : 'bg-zinc-800 text-zinc-400'
                    }`}
                  >
                    {count}
                  </span>
                </button>
              )
            })}
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-amber-500 border-t-transparent"></div>
            </div>
          ) : filteredReviews.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredReviews.map((review) => (
                <div key={review.id} className="flex">
                  <div className="w-full">
                    <ReviewCard review={review} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 text-zinc-500">
              <MessageSquare className="h-12 w-12 mx-auto text-zinc-700 mb-4" />
              <p>Der er i øjeblikket ingen anmeldelser at vise i denne kategori.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 border-t border-zinc-900 bg-gradient-to-b from-zinc-950 to-amber-950/20 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute -bottom-1/2 left-1/2 -translate-x-1/2 w-full h-full bg-[radial-gradient(ellipse_at_center,rgba(245,158,11,0.12),transparent_60%)]" />
        </div>
        <div className="relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl font-bold text-white">
            Skal din fest være den næste succes?
          </h2>
          <p className="mt-3 text-zinc-400 max-w-xl mx-auto">
            Book DJ Ron9 i dag og vær garanteret en fest, som dine gæster vil tale om i mange år fremover.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/#kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#bb9b56] px-8 py-3.5 text-sm font-bold text-white shadow-lg hover:bg-[#a88645]"
            >
              <Send className="h-4 w-4" />
              Få et uforpligtende tilbud
            </a>
            <div
              className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-full border border-zinc-800 bg-zinc-950 px-8 py-3.5 text-sm font-bold text-white"
            >
              <Phone className="h-4 w-4 text-[#bb9b56]" />
              50 99 82 82
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
